import { Upload, X } from "lucide-react";
import type React from "react";
import { useEffect, useRef } from "react";
import {
  Controller,
  type ControllerRenderProps,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { Button } from "@/v2/ui/primitives/button";
import { Label } from "@/v2/ui/primitives/label";

export type IconState = string | { file: File; preview: string } | null;

export interface FormFileUploadProps {
  name: string;
  label?: string;
  accept?: string;
  maxSize?: number;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

interface FileUploadFieldProps {
  field: ControllerRenderProps<FieldValues, string>;
  getPreviewUrl: (value: IconState) => string | null;
  handleFileSelect: (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: IconState) => void
  ) => void;
  handleRemove: (
    value: IconState,
    onChange: (value: IconState) => void
  ) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  accept: string;
  disabled: boolean;
  name: string;
  error: boolean;
  maxSize: number;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  field,
  getPreviewUrl,
  handleFileSelect,
  handleRemove,
  fileInputRef,
  accept,
  disabled,
  name,
  error,
  maxSize,
}) => {
  const previewUrl = getPreviewUrl(field.value);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      const value = field.value;
      if (value && typeof value === "object" && value.preview) {
        URL.revokeObjectURL(value.preview);
      }
    };
  }, [field.value]);

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFileSelect(e, field.onChange)}
        disabled={disabled}
        className="tw-hidden"
        id={`${name}-file-input`}
      />

      {previewUrl ? (
        <div className="tw-flex tw-items-start tw-gap-3 tw-p-3 tw-border tw-border-input tw-rounded-md tw-bg-background">
          <div className="tw-relative tw-w-24 tw-h-24 tw-rounded-md tw-border tw-border-input tw-overflow-hidden tw-flex-shrink-0 tw-bg-muted">
            <img
              src={previewUrl}
              alt="Preview"
              className="tw-w-full tw-h-full tw-object-cover"
            />
          </div>
          <div className="tw-flex-1 tw-min-w-0">
            {typeof field.value === "object" && field.value?.file && (
              <div>
                <p className="tw-text-sm tw-font-medium tw-truncate">
                  {field.value.file.name}
                </p>
                <p className="tw-text-xs tw-text-muted-foreground">
                  {(field.value.file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            )}
            {typeof field.value === "string" && (
              <p className="tw-text-sm tw-text-muted-foreground">
                {strings("form.currentFile")}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => handleRemove(field.value, field.onChange)}
            disabled={disabled}
            className="tw-flex-shrink-0"
          >
            <X className="tw-h-4 tw-w-4" />
          </Button>
        </div>
      ) : (
        <label
          htmlFor={`${name}-file-input`}
          className={cn(
            "tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-h-32 tw-border-2 tw-border-dashed tw-rounded-md tw-cursor-pointer tw-transition-colors hover:tw-bg-muted/50",
            error && "tw-border-destructive",
            disabled && "tw-opacity-50 tw-cursor-not-allowed"
          )}
        >
          <Upload className="tw-h-8 tw-w-8 tw-text-muted-foreground" />
          <div className="tw-text-center">
            <p className="tw-text-sm tw-text-muted-foreground">
              {strings("form.clickToUpload")}
            </p>
            {accept && (
              <p className="tw-text-xs tw-text-muted-foreground tw-mt-1">
                {accept} (max {(maxSize / 1024 / 1024).toFixed(1)}MB)
              </p>
            )}
          </div>
        </label>
      )}
    </div>
  );
};

export const FormFileUpload: React.FC<FormFileUploadProps> = ({
  name,
  label,
  accept = "image/*",
  maxSize = 5 * 1024 * 1024, // 5MB default
  required = false,
  disabled = false,
  className,
}) => {
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const error = errors[name];
  const errorMessage = error?.message as string | undefined;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: IconState) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (maxSize && file.size > maxSize) {
      setError(name, {
        type: "manual",
        message: `File size must be less than ${(maxSize / 1024 / 1024).toFixed(1)}MB`,
      });
      return;
    }

    // Validate file type
    if (accept && accept !== "*") {
      const acceptPatterns = accept.split(",").map((a) => a.trim());
      const isValid = acceptPatterns.some((pattern) => {
        if (pattern.startsWith(".")) {
          return file.name.toLowerCase().endsWith(pattern.toLowerCase());
        }
        const regex = new RegExp(pattern.replace(/\*/g, ".*"));
        return regex.test(file.type);
      });

      if (!isValid) {
        setError(name, {
          type: "manual",
          message: `Invalid file type. Expected: ${accept}`,
        });
        return;
      }
    }

    clearErrors(name);

    // Create preview URL
    const preview = URL.createObjectURL(file);
    onChange({ file, preview });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemove = (
    value: IconState,
    onChange: (value: IconState) => void
  ) => {
    // Clean up blob URL if it exists
    if (value && typeof value === "object" && value.preview) {
      URL.revokeObjectURL(value.preview);
    }
    onChange(null);
    clearErrors(name);
  };

  const getPreviewUrl = (value: IconState): string | null => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value.preview) return value.preview;
    return null;
  };

  return (
    <div className={cn("tw-space-y-1", className)}>
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <span className="tw-text-destructive tw-ml-1">*</span>}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <FileUploadField
            field={field}
            getPreviewUrl={getPreviewUrl}
            handleFileSelect={handleFileSelect}
            handleRemove={handleRemove}
            fileInputRef={fileInputRef}
            accept={accept}
            disabled={disabled}
            name={name}
            error={Boolean(error)}
            maxSize={maxSize}
          />
        )}
      />
      {errorMessage && (
        <p className="tw-text-sm tw-text-destructive tw-mt-1">{errorMessage}</p>
      )}
    </div>
  );
};
